import { validateForm } from "../../utility/common/TransactionComponents/TransactionValidationUtils";
import {
  PQR_GroovePage1,
  PQR_OverlayPage1,
  PQR_GroovePage2,
  PQR_OverlayPage2,
  PQR_tubePage1,
  PQR_tubePage2,
  PQR_tubePage3,
  PQR_Detail_GroovePage1,
  PQR_Detail_OverlayPage1,
  PQR_Detail_TubePage1,
  PQR_OverlayCheckbox,
} from "./PQR_entity";

/**
 * Common PQR validation function that replaces manual validation
 * @param {Object} config - Validation configuration
 * @returns {Object} Validation result with errors and isError flag
 */
export const validatePQRForm = ({
  headerObj,
  detailObj,
  WPStype,
  currentErrors = {},
  pageType = "page1", // page1, page2, page3, tubesheet
  setError = null,
  fieldPWHT = false,
}) => {
  let requiredFields = {};
  let detailConfig = [];

  // Configure validation based on WPS type and page
  switch (pageType) {
    case "page1":
      if (WPStype === "Groove") {
        requiredFields = PQR_GroovePage1;
        detailConfig = PQR_Detail_GroovePage1;
      } else {
        // Handle overlay checkbox logic
        requiredFields = fieldPWHT === false ? PQR_OverlayCheckbox : PQR_OverlayPage1;
        detailConfig = PQR_Detail_OverlayPage1;
      }
      break;
    case "page2":
      requiredFields = WPStype === "Groove" ? PQR_GroovePage2 : PQR_OverlayPage2;
      detailConfig = [];
      break;
    case "page3":
    case "tubesheet3":
      requiredFields = PQR_tubePage3;
      detailConfig = [];
      break;
    case "tubesheet1":
      requiredFields = PQR_tubePage1;
      detailConfig = PQR_Detail_TubePage1;
      break;
    case "tubesheet2":
      requiredFields = PQR_tubePage2;
      detailConfig = [];
      break;
   
    default:
      break;
  }
  // Use common validation function

  const detaildata = Array.isArray(detailObj) ? detailObj.filter((item) => item.PQR_Process !== "") : detailObj;
 
  console.log("detaildata",detaildata);
  const result = validateForm({
    headerObj,
    detailObj: detaildata,
    requiredFields,
    detailConfig,
    currentErrors,
  });

  // Update error state if setError function provided (PQR compatibility)
  if (setError && result.errors) {
    setError((prevState) => ({
      ...prevState,
      ...result.errors,
    }));
  }

  return result;
};

/**
 * PQR-specific validation for process requirements
 * @param {Array} detailObj - PQR detail array
 * @param {Function} checkPQRProcessAdded - PQR process validation function
 * @param {Function} checkduplicateProcess - PQR duplicate check function
 * @returns {Object} Validation result
 */
export const validatePQRProcesses = (detailObj, checkPQRProcessAdded, checkduplicateProcess) => {
  let isError = false;
  
  // Check if at least one process is added
  let norows = checkPQRProcessAdded(detailObj);
  if (norows) {
    detailObj[0].errormessage = "Atleast one Process and Type is required";
    isError = true;
    detailObj[0].error = true;

    if (detailObj[0].PQR_Process !== "" && detailObj[0].PQR_Type !== "") {
      detailObj[0].error = false;
    }
  }

  // Check for duplicate processes
  let duplicateprocess = checkduplicateProcess(detailObj);
  if (duplicateprocess){
    detailObj[0].errormessage = "Duplicate Process and Type";
    isError = true;
    detailObj[0].error = true;
  } 

  return { isError };
};

/**
 * PQR duplicate check validation
 * @param {Function} checkDuplicate - Duplicate check function
 * @param {string} type - PQR type
 * @param {string} proj - Project year
 * @param {string} revno - Record number
 * @returns {Object} Validation result
 */
export const validatePQRDuplicate = async (checkDuplicate, type, proj, revno) => {
  const isDuplicate = await checkDuplicate(type, proj, revno);
  if (isDuplicate) {
    return {
      isError: true,
      error: {
        PQR_RecordNo: {
          status: true,
          message: "PQR Record No. already exists",
        }
      }
    };
  }
  return {
    isError: false,
    error: {
      PQR_RecordNo: {
        status: false,
        message: "",
      }
    }
  };
};

/**
 * Unified validation for PQR Page 2
 * @param {Object} config - Validation configuration
 * @returns {Object} Validation result
 */
export const validatePQRFormPage2 = ({
  headerObj,
  detailObj = [],
  currentErrors = {},
  requiredFields = {},
  WPStype = "Groove",
  setError = null,
}) => {
  const result = validateForm({
    headerObj,
    detailObj,
    requiredFields,
    detailConfig: [],
    currentErrors,
  });

  if (setError && result.errors) {
    setError((prevState) => ({
      ...prevState,
      ...result.errors,
    }));
  }

  return result;
};

/**
 * Validates PQR test arrays (replaces manual test validation functions)
 * @param {Array} testArray - Test array to validate
 * @param {Array} requiredFields - Required field names
 * @param {number} minRows - Minimum required rows
 * @returns {boolean} True if validation passes
 */
export const validatePQRTestArray = (testArray, requiredFields, minRows = 1) => {
  if (!testArray || testArray.length === 0) return false;

  let validRows = 0;
  
  for (let i = 0; i < Math.min(testArray.length, minRows * 2); i++) {
    const row = testArray[i];
    const isRowComplete = requiredFields.every(field => 
      row[field] && row[field] !== "" && row[field] !== null
    );
    
    if (isRowComplete) validRows++;
  }

  const isValid = validRows >= minRows;
  
  // Update error state on test array
  if (testArray[0]) {
    testArray[0].error = !isValid;
    testArray[0].errormessage = isValid ? "" : `Minimum ${minRows} Full Row is required`;
  }

  return isValid;
};

/**
 * Enhanced PQR test validation with unified approach
 * @param {Array} testArray - Test array to validate
 * @param {Array} requiredFields - Required field names
 * @param {number} minRows - Minimum required rows
 * @param {Function} customValidator - Custom validation function
 * @returns {Object} Validation result
 */
export const validatePQRTestArrayEnhanced = (testArray, requiredFields, minRows = 1, customValidator = null) => {
  const isValid = validatePQRTestArray(testArray, requiredFields, minRows);
  
  let customResult = { isError: false, errors: {} };
  if (customValidator) {
    customResult = customValidator(testArray);
  }
  
  return {
    isError: !isValid || customResult.isError,
    errors: customResult.errors || {},
    validRows: isValid ? minRows : 0,
  };
};